/**
 * Student edit form.
 * Loads the student, hydrates the form, then PATCHes profile / status / establishment in one submit.
 */
import * as React from 'react';
import { User, GraduationCap, BookOpen, Clock, Users, Building2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Input } from '@/components/forms/Input';
import { RadioGroup } from '@/components/forms/RadioGroup';
import { Select } from '@/components/forms/Select';
import { MultiSelect } from '@/components/forms/MultiSelect';
import { AvailabilityGrid } from '@/components/forms/AvailabilityGrid';
import { Checkbox } from '@/components/forms/Checkbox';
import { Textarea } from '@/components/forms/Textarea';
import { DateSplitInput } from '@/components/forms/DateSplitInput';
import { SlidePanelForm } from '@/components/common/SlidePanelForm';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  getStudent,
  getStudentStatusHistory,
  getStudents,
  updateStudent,
} from '@/services/inperson/students.service';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import {
  normalizeStudentAccountStatus,
  type EditStudentFormState,
  type StudentAccountStatus,
  type UpdateStudentDto,
} from '@/types/inperson/student.types';
import { getSubjects } from '@/services/subjects.service';
import { getLevels } from '@/services/levels.service';
import { getSchoolClasses, getEstablishment } from '@/services/establishments.service';
import { useProgramsForMode } from '@/hooks/useProgramsForMode';
import { ProgramCascadeFields } from '@/components/common/ProgramCascadeFields';
import { getSpecialties } from '@/services/specialties.service';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { useAuthStore } from '@/stores/authStore';
import { useEligibleEstablishments } from '@/hooks/useUsers';
import { useEnrichEstablishmentLabels } from '@/hooks/useEnrichEstablishmentLabels';
import { useFormValidation } from '@/hooks/useFormValidation';
import { studentValidationSchema } from '@/utils/validation-schemas-utils';
import { ESTABLISHMENT_SEARCH_HINT } from '@/utils/format-establishment-select-label-utils';
import {
  DISABILITY_TYPE_OPTIONS,
  EXIT_REASON_OPTIONS,
  SPECIAL_NEEDS_TYPE_OPTIONS,
  STUDENT_FORM_QUERY_KEYS,
  STUDENT_GENDER_OPTIONS,
  BOOLEAN_SELECT_OPTIONS,
} from '@/components/students/student-configs';
import {
  appendSelectedEstablishmentOption,
  applyEditStudentField,
  buildUpdateStudentDto,
  collectProgramAllowedSlots,
  firstEstablishmentId,
  generateStudentDisplayName,
  IN_PERSON_STUDENT_ELIGIBLE_DEPARTMENTS,
  isDisplayNameTaken,
  isEditStudentFormValid,
  filterLevelsForEstablishmentType,
  mapEligibleEstablishmentOptions,
  mapIdLabelOptions,
  mapSpecialtyOptionsForLevel,
  mergeEstablishmentLabelCache,
  nextMaxNomLength,
  parseStudentReactivationTargetStatus,
  shouldApplySingleEstablishmentLock,
  shouldCheckDisplayName,
  shouldClearLastNameOnFirstNameEmpty,
  emptyEditStudentForm,
  getStudentEditStatusSelectOptions,
  resolvePreviousStatusBeforeDeactivate,
  studentToEditFormData,
  type EligibleEstablishmentOptionSource,
  type EstablishmentLabelEntry,
} from '@/components/students/inperson/student-form-utils';

/** No-op for locked / loading establishment inputs. */
function ignoreDisabledInputChange(): void {
  return;
}

/** Props for {@link EditStudentView}. */
interface EditStudentViewProps {
  studentId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

/** Student edit form. */
export const EditStudentView: React.FC<Readonly<EditStudentViewProps>> = ({
  studentId,
  onClose,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const [displayNameCheckQuery, setDisplayNameCheckQuery] = React.useState('');
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const [formData, setFormData] = React.useState<EditStudentFormState>(emptyEditStudentForm);
  const [isHydrated, setIsHydrated] = React.useState(false);

  const [maxNomLength, setMaxNomLength] = React.useState(2);
  const skipAutoGenOnLoadRef = React.useRef(false);
  const lastNameRef = React.useRef('');
  const prevFirstNameRef = React.useRef('');

  const validation = useFormValidation(studentValidationSchema);
  const formDataRef = React.useRef(formData);
  formDataRef.current = formData;

  const {
    data: student,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: STUDENT_FORM_QUERY_KEYS.student(studentId),
    queryFn: () => getStudent(studentId),
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError,
    error,
    catalogKey: 'studentNotFound',
    onClose,
    entityId: studentId,
  });

  const studentStatusNorm = student ? normalizeStudentAccountStatus(String(student.status)) : null;

  const { data: statusHistory = [] } = useQuery({
    queryKey: STUDENT_FORM_QUERY_KEYS.studentStatusHistory(studentId),
    queryFn: () => getStudentStatusHistory(studentId),
    enabled: !!studentId && studentStatusNorm === 'deactivated',
  });

  const previousStatusBeforeDeactivate = React.useMemo(
    () => resolvePreviousStatusBeforeDeactivate(statusHistory),
    [statusHistory],
  );

  const studentReactivationTargetStatus = React.useMemo(
    () => parseStudentReactivationTargetStatus(student?.reactivationTargetStatus),
    [student?.reactivationTargetStatus],
  );

  const { data: subjects = [] } = useQuery({
    queryKey: STUDENT_FORM_QUERY_KEYS.subjects,
    queryFn: async () => {
      const response = await getSubjects();
      return response.data;
    },
  });

  const selectedEstablishmentIds = formData.establishmentIds;
  const selectedEstablishmentId = firstEstablishmentId(selectedEstablishmentIds);
  const selectedLevelId = formData.levelId;

  const { user } = useAuthStore();
  const companyId = user?.companyId ?? '';
  const { data: eligibleEstablishments = [], isLoading: isLoadingEligible } =
    useEligibleEstablishments(IN_PERSON_STUDENT_ELIGIBLE_DEPARTMENTS, companyId, {
      enabled: !!companyId,
    });

  const allEstablishmentOptions = React.useMemo(
    () =>
      mapEligibleEstablishmentOptions(
        eligibleEstablishments as EligibleEstablishmentOptionSource[],
      ),
    [eligibleEstablishments],
  );

  const [establishmentLabelCache, setEstablishmentLabelCache] = React.useState<Map<string, string>>(
    new Map(),
  );
  const primeEstablishmentLabelCache = React.useCallback((entries: EstablishmentLabelEntry[]) => {
    setEstablishmentLabelCache((prev) => mergeEstablishmentLabelCache(prev, entries));
  }, []);
  useEnrichEstablishmentLabels(selectedEstablishmentIds, primeEstablishmentLabelCache);

  const establishmentOptions = React.useMemo(
    () =>
      appendSelectedEstablishmentOption(
        allEstablishmentOptions,
        selectedEstablishmentId,
        establishmentLabelCache,
      ),
    [allEstablishmentOptions, selectedEstablishmentId, establishmentLabelCache],
  );

  const isSingleEstablishmentLocked = !isLoadingEligible && allEstablishmentOptions.length === 1;
  const lockedEstablishmentId = isSingleEstablishmentLocked
    ? allEstablishmentOptions[0]?.value
    : undefined;
  const lockedEstablishmentLabel = isSingleEstablishmentLocked
    ? allEstablishmentOptions[0]?.label
    : undefined;

  /** Lock a single-establishment actor onto their assigned id so the form never submits empty. */
  React.useEffect(() => {
    if (
      !shouldApplySingleEstablishmentLock(
        isSingleEstablishmentLocked,
        lockedEstablishmentId,
        selectedEstablishmentIds,
      ) ||
      !lockedEstablishmentId
    ) {
      return;
    }
    setFormData((prev) => applyEditStudentField(prev, 'establishmentIds', [lockedEstablishmentId]));
  }, [isSingleEstablishmentLocked, lockedEstablishmentId, selectedEstablishmentIds]);

  const { data: levels = [] } = useQuery({
    queryKey: STUDENT_FORM_QUERY_KEYS.levels,
    queryFn: async () => {
      const response = await getLevels({ pageSize: 10 });
      return response.data;
    },
  });

  const { data: selectedEstablishmentType } = useQuery({
    queryKey: STUDENT_FORM_QUERY_KEYS.establishmentType(selectedEstablishmentId ?? ''),
    queryFn: async () => (await getEstablishment(selectedEstablishmentId!)).type ?? null,
    enabled: !!selectedEstablishmentId,
  });

  const allowedLevels = React.useMemo(
    () => filterLevelsForEstablishmentType(levels, selectedEstablishmentType),
    [levels, selectedEstablishmentType],
  );

  /** Changing establishment type drops a level that is no longer offered there. */
  React.useEffect(() => {
    if (!selectedLevelId || selectedEstablishmentType === undefined || levels.length === 0) return;
    if (allowedLevels.some((level) => level.id === selectedLevelId)) return;
    setFormData((prev) => applyEditStudentField(prev, 'levelId', ''));
  }, [selectedLevelId, selectedEstablishmentType, allowedLevels, levels.length]);

  const { data: programsResponse } = useProgramsForMode();
  const programsData = programsResponse?.data ?? [];

  const allowedSlots = React.useMemo(
    () =>
      collectProgramAllowedSlots(
        formData.programIds,
        programsData as Array<{
          id: string;
          availability?: { weekly?: Array<{ start: string; end: string }> };
        }>,
      ),
    [formData.programIds, programsData],
  );

  const { data: schoolClassesRes } = useQuery({
    queryKey: STUDENT_FORM_QUERY_KEYS.schoolClasses(selectedEstablishmentId ?? '', selectedLevelId),
    queryFn: async () => {
      if (!selectedLevelId) return { data: [], total: 0, page: 1, pageSize: 10 };
      return getSchoolClasses({
        establishmentId: selectedEstablishmentId || undefined,
        levelId: selectedLevelId,
        pageSize: 10,
      });
    },
    enabled: !!selectedEstablishmentId && !!selectedLevelId,
  });
  const schoolClasses = schoolClassesRes?.data ?? [];

  // Load all active specialties with their level links.
  const { data: allSpecialties = [] } = useQuery({
    queryKey: STUDENT_FORM_QUERY_KEYS.specialtiesActive,
    queryFn: () => getSpecialties(true),
  });

  const { data: displayNameCheckResult } = useQuery({
    queryKey: STUDENT_FORM_QUERY_KEYS.displayNameCheckEdit(displayNameCheckQuery),
    queryFn: () => getStudents({ search: displayNameCheckQuery, pageSize: 5 }),
    enabled: displayNameCheckQuery.length >= 2,
  });

  const displayNameAlreadyExists = React.useMemo(
    () => isDisplayNameTaken(displayNameCheckQuery, displayNameCheckResult?.data, studentId),
    [displayNameCheckQuery, displayNameCheckResult, studentId],
  );

  const specialtyOptions = React.useMemo(
    () => mapSpecialtyOptionsForLevel(allSpecialties, selectedLevelId),
    [selectedLevelId, allSpecialties],
  );

  /** Hydrate from GET student; skip the display-name autogen so the stored name is kept. */
  React.useEffect(() => {
    if (!student) return;
    const initialData = studentToEditFormData(student);
    skipAutoGenOnLoadRef.current = true;
    setFormData(initialData);
    setIsHydrated(true);
    setMaxNomLength(2);
    validation.validateInitialFormData(initialData);
  }, [student]);

  // Rebuild displayName when first or last name changes.
  React.useEffect(() => {
    if (skipAutoGenOnLoadRef.current) {
      skipAutoGenOnLoadRef.current = false;
      return;
    }
    const generated = generateStudentDisplayName(formData.firstName, formData.lastName);
    if (!generated) return;
    setFormData((prev) => ({ ...prev, displayName: generated }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (shouldCheckDisplayName(formData.lastName)) {
      debounceRef.current = setTimeout(() => {
        setDisplayNameCheckQuery(generated);
      }, 600);
    } else {
      setDisplayNameCheckQuery('');
    }
  }, [formData.firstName, formData.lastName]);

  // Keep the ref in sync with the current lastName.
  React.useEffect(() => {
    lastNameRef.current = formData.lastName;
  }, [formData.lastName]);

  // Clear last name only when first name is emptied after hydrate (not on remount).
  React.useEffect(() => {
    if (!isHydrated) return;
    const prev = prevFirstNameRef.current;
    prevFirstNameRef.current = formData.firstName;
    if (!shouldClearLastNameOnFirstNameEmpty(prev, formData.firstName)) return;
    setMaxNomLength(2);
    setFormData((s) => ({ ...s, lastName: '' }));
  }, [formData.firstName, isHydrated]);

  // Bump maxNomLength only when displayNameAlreadyExists flips true
  // (omit lastName from deps so each keystroke does not increment).
  React.useEffect(() => {
    if (displayNameAlreadyExists) {
      setMaxNomLength((prev) => nextMaxNomLength(prev, lastNameRef.current));
    }
  }, [displayNameAlreadyExists]);

  const updateMutation = useMutation({
    mutationFn: (dto: UpdateStudentDto) => updateStudent(studentId, dto),
    onSuccess: async () => {
      toast.success('Élève modifié avec succès !');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: STUDENT_FORM_QUERY_KEYS.student(studentId) }),
        queryClient.invalidateQueries({ queryKey: ['students'] }),
      ]);
      onSuccess?.();
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  /** Set one field and re-validate (clears dependent values via {@link applyEditStudentField}). */
  const handleFieldChange = (field: string, value: unknown) => {
    const next = applyEditStudentField(formDataRef.current, field, value);
    setFormData(next);
    validation.validateField(field, value, next);
    validation.markAsTouched(field);
    validation.markAsDirty(field);
  };

  const handleBlur = (field: string) => {
    validation.markAsTouched(field);
  };

  /** Persist a single establishment id (or clear). */
  function handleEstablishmentChange(val: string): void {
    handleFieldChange('establishmentIds', val ? [val] : []);
  }

  /** Mark the establishment field touched. */
  function handleEstablishmentBlur(): void {
    handleBlur('establishmentIds');
  }

  /** Loading / single-lock / eligible autocomplete for the establishment field. */
  function renderEstablishmentField(): React.ReactNode {
    if (isLoadingEligible && allEstablishmentOptions.length === 0) {
      return (
        <Input
          label="Établissement"
          value=""
          onChange={ignoreDisabledInputChange}
          placeholder="Chargement des établissements..."
          disabled
          required
          loading
        />
      );
    }
    if (isSingleEstablishmentLocked && lockedEstablishmentLabel) {
      return (
        <Input
          label="Établissement"
          value={lockedEstablishmentLabel}
          onChange={ignoreDisabledInputChange}
          disabled
          required
          helperText="Établissement assigné à votre compte"
          state={validation.getFieldState('establishmentIds')}
          error={validation.getFieldError('establishmentIds')}
        />
      );
    }
    return (
      <Autocomplete
        label="Établissement"
        placeholder="Rechercher un établissement..."
        options={establishmentOptions}
        value={selectedEstablishmentId}
        onChange={handleEstablishmentChange}
        onBlur={handleEstablishmentBlur}
        isLoading={isLoadingEligible}
        helperText={ESTABLISHMENT_SEARCH_HINT}
        required
        state={validation.getFieldState('establishmentIds')}
        error={validation.getFieldError('establishmentIds')}
        inputSize="md"
        minSearchLength={0}
      />
    );
  }

  // Stable handler for ProgramCascadeFields: functional update avoids race condition
  // with setFormData(initialData) hydration effect (child effects run after parent effects,
  // and formDataRef.current may still point to the empty state at that moment).
  const handleProgramCascadeChange = React.useCallback((field: string, value: unknown) => {
    if (field === 'tutoringType') return;
    const key = field === 'programs' ? 'programIds' : field;
    setFormData((prev) => ({ ...prev, [key]: value }));
  }, []);

  /** Validate then PATCH profile / status / establishment. */
  const handleSubmit = () => {
    const isValid = validation.validateForm(formData);

    if (!isValid) {
      toast.error(getErrorMessage('requiredFields'));
      return;
    }

    updateMutation.mutate(buildUpdateStudentDto(formData));
  };

  const subjectOptions = mapIdLabelOptions(subjects);
  const levelOptions = mapIdLabelOptions(allowedLevels);

  const genderOptions = [...STUDENT_GENDER_OPTIONS];

  const canSubmit = isEditStudentFormValid(formData, displayNameAlreadyExists);

  return (
    <SlidePanelForm
      formType="edit"
      title="Modifier l'élève"
      subtitle="Modifiez les informations de l'élève"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Enregistrer"
      isSubmitting={updateMutation.isPending}
      isValid={canSubmit}
      isLoading={isLoading || (!!student && !isHydrated)}
    >
      <div className="form-section">
        <h4 className="form-section-title">
          <User className="w-4 h-4" />
          Informations personnelles
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Prénom"
            value={formData.firstName}
            onChange={(e) => handleFieldChange('firstName', e.target.value)}
            onBlur={() => handleBlur('firstName')}
            placeholder="Ex: Marie"
            maxLength={100}
            required
            state={validation.getFieldState('firstName')}
            error={validation.getFieldError('firstName')}
          />
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className={!formData.firstName.trim() ? 'cursor-not-allowed' : ''}>
                  <Input
                    label="Nom"
                    value={formData.lastName}
                    onChange={(e) =>
                      handleFieldChange('lastName', e.target.value.slice(0, maxNomLength))
                    }
                    onBlur={() => handleBlur('lastName')}
                    placeholder="Ex: MA"
                    maxLength={maxNomLength}
                    required
                    disabled={!formData.firstName.trim()}
                    state={
                      displayNameAlreadyExists ? 'error' : validation.getFieldState('lastName')
                    }
                    error={
                      displayNameAlreadyExists
                        ? getErrorMessage('displayNameAlreadyExists')
                        : validation.getFieldError('lastName')
                    }
                  />
                </span>
              </TooltipTrigger>
              {!formData.firstName.trim() && (
                <TooltipContent side="top">
                  <p>Saisissez d'abord le prénom</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
          <p className="col-span-full text-xs text-muted-foreground -mt-2">
            Le nom de famille est limité à 2 caractères (anonymisation des élèves).
          </p>
          {/* Date of birth — hidden */}
          <div className="col-span-full hidden">
            <DateSplitInput
              label="Date de naissance"
              value={formData.dateOfBirth}
              onChange={(value) => handleFieldChange('dateOfBirth', value)}
              onBlur={() => handleBlur('dateOfBirth')}
              maxDate={`${new Date().getFullYear() - 3}-12-31`}
            />
          </div>
          <div className="col-span-full">
            <Select
              label="Genre"
              value={formData.gender}
              onChange={(value) => handleFieldChange('gender', value)}
              onBlur={() => handleBlur('gender')}
              options={genderOptions}
              placeholder="Sélectionner"
              required
              state={validation.getFieldState('gender')}
              error={validation.getFieldError('gender')}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 mt-4">
          <Select
            label="Statut"
            value={formData.status}
            onChange={(value) => handleFieldChange('status', value)}
            onBlur={() => handleBlur('status')}
            options={getStudentEditStatusSelectOptions(
              formData.status as StudentAccountStatus,
              formData.status === 'deactivated'
                ? {
                    previousStatusBeforeDeactivate,
                    reactivationTargetStatus: studentReactivationTargetStatus,
                  }
                : undefined,
            )}
            state={validation.getFieldState('status')}
            error={validation.getFieldError('status')}
          />
          {formData.status === 'deactivated' && (
            <>
              <DateSplitInput
                label="Date de désactivation"
                value={formData.exitDate}
                onChange={(value) => handleFieldChange('exitDate', value)}
                onBlur={() => handleBlur('exitDate')}
                maxDate="2099-12-31"
                required
                state={validation.getFieldState('exitDate')}
                error={validation.getFieldError('exitDate')}
              />
              <Select
                label="Motif de sortie"
                value={formData.deactivationReasonType}
                onChange={(v) => handleFieldChange('deactivationReasonType', v)}
                options={[{ value: '', label: 'Sélectionner un motif…' }, ...EXIT_REASON_OPTIONS]}
              />
              {formData.deactivationReasonType === 'other' && (
                <Input
                  label="Préciser le motif"
                  value={formData.deactivationReasonOther}
                  onChange={(e) => handleFieldChange('deactivationReasonOther', e.target.value)}
                  onBlur={() => handleBlur('deactivationReasonOther')}
                  placeholder="Précisez le motif…"
                  maxLength={200}
                  required
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Address section hidden (GDPR). */}

      <div className="form-section">
        <h4 className="form-section-title">
          <Building2 className="w-4 h-4" />
          Établissement
        </h4>
        <div className="grid grid-cols-1 gap-4">{renderEstablishmentField()}</div>
      </div>

      {selectedEstablishmentIds.length > 0 && (
        <div className="form-section">
          <h4 className="form-section-title">
            <GraduationCap className="w-4 h-4" />
            Scolarité
          </h4>
          <div className="grid grid-cols-1 gap-4">
            <Select
              label="Niveaux académiques"
              placeholder={
                allowedLevels.length === 0 ? 'Aucun niveau disponible' : 'Sélectionner un niveau'
              }
              options={levelOptions}
              value={formData.levelId}
              onChange={(value) => handleFieldChange('levelId', value)}
              onBlur={() => handleBlur('levelId')}
              required
              state={validation.getFieldState('levelId')}
              error={validation.getFieldError('levelId')}
            />

            {!!selectedLevelId && (
              <Select
                label="Classe"
                value={formData.schoolClassId}
                onChange={(value) => handleFieldChange('schoolClassId', value)}
                onBlur={() => handleBlur('schoolClassId')}
                options={schoolClasses.map((sc) => ({ value: sc.id, label: sc.name }))}
                placeholder={
                  schoolClasses.length === 0
                    ? 'Aucune classe disponible pour ce niveau'
                    : 'Sélectionner une classe (optionnel)'
                }
              />
            )}

            {specialtyOptions.length > 0 && (
              <MultiSelect
                label="Spécialités"
                placeholder="Sélectionnez les spécialités"
                options={specialtyOptions}
                value={formData.specialtyIds}
                onChange={(values) => handleFieldChange('specialtyIds', values)}
                onBlur={() => handleBlur('specialtyIds')}
                maxVisibleTags={3}
                helperText="Spécialités disponibles pour ce niveau"
              />
            )}

            <div className="space-y-3">
              <div className="flex items-end pb-2">
                <Checkbox
                  id="hasSpecialNeeds"
                  label="A des besoins spécifiques"
                  checked={formData.hasSpecialNeeds}
                  onCheckedChange={(checked) => {
                    handleFieldChange('hasSpecialNeeds', checked);
                    if (!checked) {
                      handleFieldChange('specialNeedsType', '');
                      handleFieldChange('specialNeedsNote', '');
                    }
                  }}
                />
              </div>
              {formData.hasSpecialNeeds && (
                <div className="space-y-3 pl-1">
                  <Select
                    label="Type de besoin spécifique"
                    value={formData.specialNeedsType}
                    onChange={(value) => {
                      handleFieldChange('specialNeedsType', value);
                      if (value !== 'autre') handleFieldChange('specialNeedsNote', '');
                    }}
                    onBlur={() => handleBlur('specialNeedsType')}
                    options={SPECIAL_NEEDS_TYPE_OPTIONS}
                    placeholder="Sélectionner le type"
                    state={validation.getFieldState('specialNeedsType')}
                    error={validation.getFieldError('specialNeedsType')}
                  />
                  {formData.specialNeedsType === 'autre' && (
                    <Input
                      label="Préciser le besoin"
                      value={formData.specialNeedsNote}
                      onChange={(e) => handleFieldChange('specialNeedsNote', e.target.value)}
                      onBlur={() => handleBlur('specialNeedsNote')}
                      placeholder="Décrivez le besoin spécifique"
                      maxLength={200}
                      state={validation.getFieldState('specialNeedsNote')}
                      error={validation.getFieldError('specialNeedsNote')}
                    />
                  )}
                </div>
              )}
            </div>

            <div className="space-y-3">
              <RadioGroup
                name="isDisabled"
                label="Élève en situation de handicap ?"
                layout="horizontal"
                options={BOOLEAN_SELECT_OPTIONS}
                value={formData.isDisabled == null ? undefined : String(formData.isDisabled)}
                onValueChange={(v) => {
                  if (v === 'true') {
                    handleFieldChange('isDisabled', true);
                    return;
                  }
                  handleFieldChange('isDisabled', false);
                  handleFieldChange('disabilityTypes', []);
                  handleFieldChange('disabilityOtherNote', '');
                }}
              />
              {formData.isDisabled && (
                <div className="space-y-3 pl-1">
                  <MultiSelect
                    label="Types de handicap"
                    placeholder="Sélectionner les types"
                    options={DISABILITY_TYPE_OPTIONS}
                    value={formData.disabilityTypes}
                    onChange={(values) => {
                      handleFieldChange('disabilityTypes', values);
                      if (!values.includes('autre')) handleFieldChange('disabilityOtherNote', '');
                    }}
                    maxVisibleTags={4}
                  />
                  {formData.disabilityTypes.includes('autre') && (
                    <Input
                      label="Préciser le type de handicap"
                      value={formData.disabilityOtherNote}
                      onChange={(e) => handleFieldChange('disabilityOtherNote', e.target.value)}
                      placeholder="Décrivez le type de handicap"
                      maxLength={200}
                      required
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ProgramCascadeFields
        programs={programsData as any[]}
        value={{
          tutoringType: 'in_person',
          coursePath: formData.coursePath,
          programs: formData.programIds,
        }}
        onChange={handleProgramCascadeChange}
        showTutoringType={false}
      />

      <div className="form-section">
        <h4 className="form-section-title">
          <BookOpen className="w-4 h-4" />
          Matières suivies
        </h4>
        <MultiSelect
          label="Matières"
          placeholder="Sélectionnez les matières"
          options={subjectOptions}
          value={formData.subjects}
          onChange={(values) => handleFieldChange('subjects', values)}
          onBlur={() => handleBlur('subjects')}
          maxVisibleTags={3}
          helperText="Sélectionnez les matières que l'élève suit"
          state={validation.getFieldState('subjects')}
          error={validation.getFieldError('subjects')}
        />
      </div>

      <div className="form-section">
        <h4 className="form-section-title">
          <Clock className="w-4 h-4" />
          Disponibilités
        </h4>
        <AvailabilityGrid
          value={formData.availability}
          onChange={(value) => handleFieldChange('availability', value)}
          disabled={false}
          allowedSlots={allowedSlots}
        />
        <p className="text-xs text-muted-foreground mt-2">
          Sélectionnez les créneaux de présence hebdomadaires de l&apos;élève
        </p>
      </div>

      <div className="form-section">
        <h4 className="form-section-title">
          <Users className="w-4 h-4" />
          Notes
        </h4>
        <Textarea
          label="Notes"
          value={formData.medicalNotes}
          onChange={(e) => handleFieldChange('medicalNotes', e.target.value)}
          onBlur={() => handleBlur('medicalNotes')}
          placeholder="Informations ou commentaires sur l'élève"
          maxLength={500}
          rows={3}
          state={validation.getFieldState('medicalNotes')}
          error={validation.getFieldError('medicalNotes')}
          helperText="Informations médicales importantes à connaître"
        />
      </div>
    </SlidePanelForm>
  );
};
